import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { ttt } from '@xingternal/malt-tracking';

import { Headline, BodyCopy } from '@xingternal/typography';
import { FormField } from '@xingternal/text-field';
import { Checkbox } from '@xingternal/checkbox';
import { TextButton } from '@xingternal/button';
import IconGoogle from './IconGoogle';

import PasswordInput from './../password-input/PasswordInput';
import FormMessage from './../form-message/FormMessage';
import PartnerNotice from './PartnerNotice';
import { spaceM, spaceXL } from '@xingternal/tokens';

import {
  HideSmallerThanTablet,
  InputActions,
  InputWrapper,
  SubmitButton,
  TextCenter,
  Label,
} from './../styled';

import useSignupLink from './useSignupLink';

import messages from './messages';

const clearError = (fieldName, errors) =>
  Object.keys(errors).reduce((acc, field) => {
    if (field === fieldName) return acc;
    acc[field] = errors[field];
    return acc;
  }, {});

const View = ({
  errors,
  externalUrl,
  externalLoginUrl,
  headlineMessage,
  formRef,
  handleBlur,
  handleChange,
  handleSubmit,
  intl,
  noHeadline,
  noSignupLink,
  isSubmitting,
  setErrors,
  submitCount,
  touched,
  values,
  values: { username, password, perm },
  authPartner,
  isLoginDomain,
}) => {
  const location = useLocation();
  const isTouched = property => {
    const changed = touched[property] && values[property] !== '';
    const submitted = submitCount > 0;
    return changed || submitted;
  };

  const maybeError = property => {
    if (!errors[property]) return null;
    if (!isTouched(property)) return null;

    return typeof errors[property] === 'string'
      ? errors[property]
      : intl.formatMessage(errors[property]);
  };

  const onHandleChange = e => {
    handleChange(e);

    const fieldName = e.target.name;
    setErrors(clearError(fieldName, errors));
  };

  const RecoveryLinkTag = isLoginDomain ? Link : 'a';
  const recoveryLinkProps = isLoginDomain
    ? { to: '/recovery' }
    : { href: `${externalLoginUrl}/recovery` };

  const params = new URLSearchParams(location.search)
  const dest_url = params.get('dest_url')

  return (
    <React.Fragment>
      {!noHeadline && (
        <Headline size="xxlarge" as="h2">
          {intl.formatMessage(headlineMessage || messages.headline)}
        </Headline>
      )}

      <PartnerNotice partner={authPartner} />

      <form ref={formRef} method={'POST'} onSubmit={handleSubmit}>
        <InputWrapper>
          <FormField
            autoFocus
            autoCapitalize={'none'}
            size="medium"
            error={maybeError('username')}
            helperText={maybeError('username')}
            name={'username'}
            id={'username'}
            onBlur={handleBlur}
            onChange={onHandleChange}
            placeholder={intl.formatMessage(messages.usernameLabel)}
            value={username}
            data-qa={'username'}
          />
        </InputWrapper>

        <InputWrapper>
          <PasswordInput
            name={'password'}
            id={'password'}
            onBlur={handleBlur}
            onChange={onHandleChange}
            error={maybeError('password')}
            helperText={maybeError('password')}
            placeholder={intl.formatMessage(messages.passwordLabel)}
            value={password}
            data-qa={'password'}
          />
        </InputWrapper>

        <InputActions>
          <HideSmallerThanTablet>
            <Checkbox
              id={'perm'}
              name={'perm'}
              onChange={onHandleChange}
              checked={perm}
              data-qa={'perm'}
            />
            <Label htmlFor={'perm'}>
              {intl.formatMessage(messages.rememberMe)}
            </Label>
          </HideSmallerThanTablet>

          <RecoveryLinkTag
            {...recoveryLinkProps}
            data-qa={'login-problems-link'}
          >
            <BodyCopy as="span" size="medium">
              {intl.formatMessage(messages.problemsLoggingIn)}
            </BodyCopy>
          </RecoveryLinkTag>
        </InputActions>

        {submitCount > 0 && errors.general && (
          <FormMessage error={maybeError('general')} />
        )}

        <SubmitButton
          variant="primary"
          size="medium"
          loading={isSubmitting}
          onClick={handleSubmit}
          type={'submit'}
        >
          {intl.formatMessage(messages.submitLabel)}
        </SubmitButton>

        <TextCenter
        style={{ marginTop: spaceXL }}>
          <SubmitButton
            variant="secondary"
            size="medium"
            icon={IconGoogle}
            href={`${externalUrl}/signup/oauth?caller=login_page&dest_url=${dest_url}`}
            onClick={() => {
              ttt.pageview(
                'wbm/Welcome',
                'wbm/Welcome/login/google',
                {},
                {
                  PropTrackAction: 'lp_google_click',
                }
              );
            }}
          >
            {intl.formatMessage(messages.continueWithGoogle)}
          </SubmitButton>
        </TextCenter>
        
        <BodyCopy
          size="small"
          style={{ marginTop: spaceM }}
        >
          <FormattedMessage
            {...messages.tandcDescription}
            values={{
              tandc: (
                <a href={'/terms'} target={'_blank'}>
                  <FormattedMessage {...messages.tandcLabel} />
                </a>
              ),
              dataprotection: (
                <a
                  href={intl.formatMessage(messages.dataprotectionLink)}
                  target={'_blank'}
                >
                  <FormattedMessage {...messages.dataprotectionLabel} />
                </a>
              ),
            }}
          />
        </BodyCopy>

        {!noSignupLink && (
          <TextCenter>
            <TextButton
              sizeonWide="medium"
              sizeonconfined="medium"
              size="small"
              href={useSignupLink(externalUrl, location)}
            >
              {intl.formatMessage(messages.registerNow)}
            </TextButton>
          </TextCenter>
        )}
      </form>
    </React.Fragment>
  );
};

View.propTypes = {
  errors: PropTypes.object,
  externalLoginUrl: PropTypes.string.isRequired,
  externalUrl: PropTypes.string.isRequired,
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  intl: PropTypes.any,
  isSubmitting: PropTypes.bool,
  noHeadline: PropTypes.bool,
  noSignupLink: PropTypes.bool,
  setErrors: PropTypes.func,
  submitCount: PropTypes.number,
  touched: PropTypes.any,
  values: PropTypes.object,
  isLoginDomain: PropTypes.bool,
  headlineMessage: PropTypes.shape({
    id: PropTypes.string,
    message: PropTypes.string,
  }),
  authPartner: PropTypes.shape({
    website: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default injectIntl(View);
